import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tagopsService } from '../services/tagopsService';
import { useAuth } from './AuthContext';

interface ScanSettings {
  status: string;
  utc_cron?: string;
  cron?: string;
  timezone?: string;
  offset?: number;
  [key: string]: any;
}

interface NotificationScanContextType {
  scanSettings: ScanSettings | null;
  loading: boolean;
  error: string | null;
  lastFetched: Date | null;
  refreshScanSettings: () => Promise<void>;
  isStale: boolean;
  // Scan notification logic
  checkScanStart: () => Promise<{
    shouldShowNotification: boolean;
    scanMessage?: string;
  }>;
}

const NotificationScanContext = createContext<NotificationScanContextType | undefined>(undefined);

// Cache duration: 10 minutes
const SCAN_SETTINGS_CACHE_DURATION = 10 * 60 * 1000;

// Cache key for localStorage
const SCAN_SETTINGS_CACHE_KEY = 'tagops_scan_settings_cache';
const ACTIVITY_CACHE_KEY = 'tagops_last_activity';

interface CachedData {
  data: ScanSettings;
  timestamp: number;
}

export const useNotificationScan = () => {
  const context = useContext(NotificationScanContext);
  if (context === undefined) {
    throw new Error('useNotificationScan must be used within a NotificationScanProvider');
  }
  return context;
};

interface NotificationScanProviderProps {
  children: React.ReactNode;
}

export const NotificationScanProvider: React.FC<NotificationScanProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [scanSettings, setScanSettings] = useState<ScanSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  // Check if data is stale (older than cache duration)
  const isStale = lastFetched ? (Date.now() - lastFetched.getTime()) > SCAN_SETTINGS_CACHE_DURATION : true;

  // Load data from cache on mount
  useEffect(() => {
    const loadFromCache = () => {
      try {
        const cached = localStorage.getItem(SCAN_SETTINGS_CACHE_KEY);
        if (cached) {
          const { data, timestamp }: CachedData = JSON.parse(cached);
          const cacheAge = Date.now() - timestamp;
          
          if (cacheAge < SCAN_SETTINGS_CACHE_DURATION) {
            // Cache is still valid
            setScanSettings(data);
            setLastFetched(new Date(timestamp));
            setError(null);
          } else {
            // Cache is stale, remove it and clear state
            localStorage.removeItem(SCAN_SETTINGS_CACHE_KEY);
            setScanSettings(null);
            setLastFetched(null);
            setError(null);
          }
        } else {
          // No cache, clear state
          setScanSettings(null);
          setLastFetched(null);
          setError(null);
        }
      } catch (err) {
        // Invalid cache data, remove it and clear state
        localStorage.removeItem(SCAN_SETTINGS_CACHE_KEY);
        setScanSettings(null);
        setLastFetched(null);
        setError(null);
      }
    };

    loadFromCache();
  }, []);

  // Track user activity for smart refresh
  useEffect(() => {
    const updateActivity = () => {
      localStorage.setItem(ACTIVITY_CACHE_KEY, Date.now().toString());
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, updateActivity, true);
    });

    updateActivity();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  // Save data to cache
  const saveToCache = useCallback((data: ScanSettings) => {
    try {
      const cacheData: CachedData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(SCAN_SETTINGS_CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      // localStorage might be full or unavailable, ignore
    }
  }, []);

  // Fetch scan settings from API
  const fetchScanSettings = useCallback(async (): Promise<ScanSettings> => {
    // Check if we have authentication tokens before making API calls
    const idToken = localStorage.getItem('id_token');
    const accessToken = localStorage.getItem('access_token');
    
    if (!idToken && !accessToken) {
      throw new Error('No authentication tokens found');
    }

    const response = await tagopsService.getScanSettings();
    return response.data || response;
  }, []);

  // Refresh scan settings data (force fetch from API)
  const refreshScanSettings = useCallback(async (isBackgroundRefresh = false) => {
    if (!isBackgroundRefresh) {
      setLoading(true);
    }
    setError(null);
    
    try {
      const data = await fetchScanSettings();
      setScanSettings(data);
      setLastFetched(new Date());
      saveToCache(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch scan settings';
      
      // Check if it's an authentication error
      if (errorMessage.includes('401') || errorMessage.includes('403') || errorMessage.includes('Unauthorized')) {
        // Session expired, clear cache and don't show error for background refresh
        if (!isBackgroundRefresh) {
          setError('Session expired. Please refresh the page.');
        }
        localStorage.removeItem(SCAN_SETTINGS_CACHE_KEY);
        setScanSettings(null);
        setLastFetched(null);
      } else {
        setError(errorMessage);
      }
      
      if (!isBackgroundRefresh) {
        throw err;
      }
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      }
    }
  }, [fetchScanSettings, saveToCache]);

  // Check if user is active
  const isUserActive = useCallback(() => {
    const lastActivity = localStorage.getItem(ACTIVITY_CACHE_KEY);
    const now = Date.now();
    const activityThreshold = 10 * 60 * 1000; // 10 minutes
    const isTabVisible = !document.hidden;
    
    return isTabVisible && (!lastActivity || (now - parseInt(lastActivity)) < activityThreshold);
  }, []);

  // Check if scan should start and show notification
  const checkScanStart = useCallback(async (): Promise<{
    shouldShowNotification: boolean;
    scanMessage?: string;
  }> => {
    try {
      // If we don't have scan settings or they're stale, try to refresh
      if (!scanSettings || isStale) {
        if (isUserActive()) {
          await refreshScanSettings(true);
        }
      }

      // If still no scan settings, return false
      if (!scanSettings) {
        return { shouldShowNotification: false };
      }

      // Check if scan is enabled and if it should be running based on cron schedule
      const isEnabled = scanSettings.status === 'ENABLED';
      const cronExpression = scanSettings.utc_cron || scanSettings.cron;
      
      // Don't show notification if:
      // 1. Scan is disabled
      // 2. No cron expression
      if (!isEnabled || !cronExpression) {
        return { shouldShowNotification: false };
      }
      
      // Parse cron expression to determine if scan should be running now
      // Cron format: "minute hour * * ? *"
      const cronParts = cronExpression.split(' ');
      
      if (cronParts.length >= 2) {
        const scheduledMinute = parseInt(cronParts[0]);
        const scheduledHour = parseInt(cronParts[1]);
        
        const now = new Date();
        const currentMinute = now.getUTCMinutes();
        const currentHour = now.getUTCHours();
        
        // Check if current time matches the scheduled time (within a 5-minute window)
        const timeDiff = Math.abs((currentHour * 60 + currentMinute) - (scheduledHour * 60 + scheduledMinute));
        const isRunning = timeDiff <= 5; // Within 5 minutes of scheduled time
        
        // If scan should be running
        if (isRunning) {
          const startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), scheduledHour, scheduledMinute);
          const scanMessage = `A scheduled scan has started at ${startTime.toLocaleString()}.`;
          
          return {
            shouldShowNotification: true,
            scanMessage
          };
        }
      }

      return { shouldShowNotification: false };
    } catch (error) {
      console.error('Failed to check scan start:', error);
      return { shouldShowNotification: false };
    }
  }, [scanSettings, isStale, isUserActive, refreshScanSettings]);

  // Initial fetch if no valid cache, but only when user is authenticated
  useEffect(() => {
    if (user && !scanSettings && !loading && !error) {
      // No data, not loading, and no error - fetch initial data
      refreshScanSettings().catch(() => {
        // Error handling is done in refreshScanSettings
      });
    }
  }, [user, scanSettings, loading, error, refreshScanSettings]);

  // Auto-refresh if data is stale and we have cached data
  useEffect(() => {
    if (scanSettings && isStale && !loading && isUserActive()) {
      // User is active and tab is visible, refresh in background
      refreshScanSettings(true).catch(() => {
        // Ignore errors for background refresh
      });
    }
  }, [scanSettings, isStale, loading, isUserActive, refreshScanSettings]);

  const value: NotificationScanContextType = {
    scanSettings,
    loading,
    error,
    lastFetched,
    refreshScanSettings,
    isStale,
    checkScanStart,
  };

  return (
    <NotificationScanContext.Provider value={value}>
      {children}
    </NotificationScanContext.Provider>
  );
};
